import { useState, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Send, CheckCircle2, Loader2, Sun } from 'lucide-react';
import { toast } from 'sonner';

const MOUNT_TIME = Date.now;

export default function SummerCampsInterestForm() {
  const { api } = useAuth();
  const mountedAt = useRef(Date.now());
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [hp, setHp] = useState('');

  const set = (field) => (e) => setFormData(prev => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (hp) return; // honeypot
    setSubmitting(true);
    try {
      await api.post('/contact/', {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: formData.message || 'Interested in Summer 2027 camps',
        subject: 'Summer 2027 — Notify Me',
        source: 'summer_camps_interest',
        elapsed_ms: Date.now() - mountedAt.current,
      });
    } catch {
      // Silent — same pattern as division pages
    } finally {
      setSubmitting(false);
      setSubmitted(true);
    }
  };

  if (submitted) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center gap-4">
        <CheckCircle2 className="h-12 w-12 text-emerald-500" />
        <h3 className="text-xl font-extrabold text-rsc-brand-blue">You're on the list!</h3>
        <p className="text-rsc-charcoal max-w-sm">
          We'll reach out as soon as Summer 2027 registration opens. Talk soon!
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Honeypot */}
      <input
        type="text"
        name="website"
        value={hp}
        onChange={e => setHp(e.target.value)}
        tabIndex={-1}
        aria-hidden="true"
        className="hidden"
        autoComplete="off"
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="sc-name">Your Name <span className="text-red-500">*</span></Label>
          <Input
            id="sc-name"
            value={formData.name}
            onChange={set('name')}
            placeholder="Jane Smith"
            required
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="sc-email">Email <span className="text-red-500">*</span></Label>
          <Input
            id="sc-email"
            type="email"
            value={formData.email}
            onChange={set('email')}
            placeholder="jane@email.com"
            required
          />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="sc-phone">Phone (optional)</Label>
        <Input
          id="sc-phone"
          type="tel"
          value={formData.phone}
          onChange={set('phone')}
          placeholder="(555) 555-5555"
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="sc-message">Anything we should know?</Label>
        <Textarea
          id="sc-message"
          value={formData.message}
          onChange={set('message')}
          placeholder="Number of kids, grade levels, preferred location, questions…"
          rows={3}
        />
      </div>

      <Button
        type="submit"
        disabled={submitting}
        className="w-full bg-rsc-yellow text-rsc-navy font-extrabold hover:bg-rsc-yellow/90"
        size="lg"
      >
        {submitting
          ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Sending…</>
          : <><Send className="h-4 w-4 mr-2" /> Notify Me for Summer 2027</>
        }
      </Button>
    </form>
  );
}
